import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface WalkthroughStep {
  id: number
  title: string
  description: string
  tip: string
  mockupType: string
}

interface FeatureWalkthroughModalProps {
  title: string
  icon: 'mic' | 'ramble' | 'document' | 'workflow'
  steps: WalkthroughStep[]
  accentColor: string
  accentGradient: string
  onClose: () => void
}

// Header icon for the modal title bar
function ModalIcon({ type }: { type: string }) {
  const props = { viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: 2, className: 'w-5 h-5' }
  switch (type) {
    case 'mic':
      return (
        <svg {...props}>
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
          <path d="M19 10v2a7 7 0 01-14 0v-2" />
        </svg>
      )
    case 'ramble':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      )
    case 'document':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      )
    case 'workflow':
      return (
        <svg {...props}>
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      )
    default:
      return null
  }
}

// Step mockup illustrations - themed with accent color
function StepMockup({ mockupType, color }: { mockupType: string; color: string }) {
  const mockups: Record<string, React.ReactNode> = {
    'mic-setup': (
      <div className="bg-[#0f1318] rounded-xl p-6 w-full max-w-[280px] border border-white/10">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-6 h-6">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-medium text-brand-light">Ready to record</div>
            <div className="text-xs text-brand-subtle">Select your input</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3 border border-white/5" style={{ borderColor: `${color}40` }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-4 h-4">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
            </svg>
            <span className="text-xs text-brand-muted">In-person meeting</span>
            <div className="w-3 h-3 rounded-full ml-auto" style={{ background: color }} />
          </div>
          <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" className="w-4 h-4">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" />
            </svg>
            <span className="text-xs text-brand-muted">Video call</span>
          </div>
          <div className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" className="w-4 h-4">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3" />
            </svg>
            <span className="text-xs text-brand-muted">Phone call</span>
          </div>
        </div>
      </div>
    ),

    'recording': (
      <div className="bg-[#0f1318] rounded-xl p-6 w-full max-w-[280px] border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: color }} />
          <span className="text-sm font-medium text-brand-light">Recording in progress</span>
          <span className="text-sm text-brand-subtle ml-auto font-mono">08:42</span>
        </div>
        <div className="bg-white/5 rounded-lg p-4 mb-4">
          <div className="flex items-end justify-center gap-1 h-12">
            {[3, 5, 8, 4, 6, 9, 5, 3, 7, 4, 6, 8, 5, 3, 6].map((h, i) => (
              <div key={i} className="w-1.5 rounded-full transition-all" style={{ height: h * 4, background: color }} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-brand-subtle">
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-4 h-4">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83" />
          </svg>
          <span>AI is listening and transcribing...</span>
        </div>
      </div>
    ),

    'processing': (
      <div className="bg-[#0f1318] rounded-xl p-6 w-full max-w-[280px] border border-white/10">
        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: `${color}20` }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-7 h-7 animate-spin">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
          <div className="text-base font-medium text-brand-light mb-1">AI is working</div>
          <div className="text-sm text-brand-subtle">Structuring your content...</div>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-2">
          <div className="h-full rounded-full transition-all" style={{ width: '65%', background: `linear-gradient(90deg, ${color}, ${color}cc)` }} />
        </div>
        <div className="text-sm text-brand-muted text-center">65% complete</div>
      </div>
    ),

    'notes-ready': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-brand-light">Meeting Notes</span>
          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: `${color}20`, color }}>Complete</span>
        </div>
        <div className="space-y-3 mb-4">
          {['Client Objectives', 'Key Discussion Points', 'Agreed Actions', 'Next Steps'].map((section) => (
            <div key={section} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
              <div>
                <div className="text-xs font-semibold text-brand-light">{section}</div>
                <div className="text-xs text-brand-subtle mt-0.5">AI-generated summary...</div>
              </div>
            </div>
          ))}
        </div>
        <button className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg font-semibold w-full justify-center" style={{ background: color, color: '#0f1318' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6" />
          </svg>
          Generate Document
        </button>
      </div>
    ),

    'ramble-start': (
      <div className="bg-[#0f1318] rounded-xl p-6 w-full max-w-[280px] border border-white/10">
        <div className="text-center mb-5">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border-2 cursor-pointer transition-all hover:scale-105" style={{ borderColor: color, background: `${color}15` }}>
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-8 h-8">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2" />
            </svg>
          </div>
          <div className="text-base font-medium text-brand-light mb-1">Ramble Mode</div>
          <div className="text-sm text-brand-subtle">Tap to start speaking</div>
        </div>
        <div className="bg-white/5 rounded-lg p-3 text-center">
          <div className="flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-4 h-4">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            <span className="text-xs text-brand-muted">Speak naturally — AI will structure it</span>
          </div>
        </div>
      </div>
    ),

    'document-export': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-brand-light">Document Ready</span>
          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: `${color}20`, color }}>Approved</span>
        </div>
        <div className="bg-white/5 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" className="w-8 h-8">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div>
              <div className="text-sm text-brand-light font-medium">Suitability Letter</div>
              <div className="text-xs text-brand-subtle">PDF • 3 pages</div>
            </div>
          </div>
          <div className="h-px bg-white/10 mb-3" />
          <div className="flex gap-2">
            <div className="h-1.5 flex-1 rounded-full" style={{ background: `${color}40` }} />
            <div className="h-1.5 flex-1 rounded-full" style={{ background: `${color}30` }} />
            <div className="h-1.5 flex-1 rounded-full" style={{ background: `${color}20` }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg font-medium" style={{ background: color, color: '#0f1318' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Export PDF
          </button>
          <button className="flex items-center justify-center gap-1.5 text-xs px-3 py-2 rounded-lg font-medium bg-white/10 text-brand-light">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
            Share
          </button>
        </div>
      </div>
    ),

    'template-grid': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="text-sm font-medium text-brand-light mb-3">Choose a template</div>
        <div className="grid grid-cols-2 gap-2">
          {['Suitability Letter', 'File Notes', 'Client Report', 'Recommendation'].map((name, i) => (
            <div
              key={name}
              className="p-3 rounded-lg border transition-all cursor-pointer text-center"
              style={{
                background: i === 0 ? `${color}10` : 'rgba(255,255,255,0.03)',
                borderColor: i === 0 ? `${color}40` : 'rgba(255,255,255,0.06)',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke={i === 0 ? color : 'rgba(255,255,255,0.3)'} strokeWidth="1.5" className="w-6 h-6 mx-auto mb-2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div className="text-xs text-brand-muted leading-tight">{name}</div>
            </div>
          ))}
        </div>
      </div>
    ),

    'client-select': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[280px] border border-white/10">
        <div className="text-sm font-medium text-brand-light mb-3">Select client</div>
        <div className="bg-white/5 rounded-lg p-2.5 mb-3 flex items-center gap-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" className="w-4 h-4">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="text-xs text-brand-subtle">Search clients...</span>
        </div>
        <div className="space-y-1.5">
          {[
            { name: 'James Richardson', detail: 'Last meeting: 2 days ago' },
            { name: 'Sarah Mitchell', detail: 'Last meeting: 1 week ago' },
            { name: 'David Thompson', detail: 'Last meeting: 3 weeks ago' },
          ].map((client, i) => (
            <div
              key={client.name}
              className="flex items-center gap-3 p-2.5 rounded-lg transition-all cursor-pointer"
              style={{
                background: i === 0 ? `${color}10` : 'transparent',
                border: i === 0 ? `1px solid ${color}30` : '1px solid transparent',
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: i === 0 ? `${color}30` : 'rgba(255,255,255,0.1)', color: i === 0 ? color : 'rgba(255,255,255,0.5)' }}>
                {client.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <div className="text-xs font-medium text-brand-light">{client.name}</div>
                <div className="text-[10px] text-brand-subtle">{client.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),

    'editor': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
          <div className="flex gap-1.5">
            {['B', 'I', 'U'].map(btn => (
              <div key={btn} className="w-7 h-7 rounded bg-white/5 flex items-center justify-center text-xs text-brand-muted font-semibold cursor-pointer hover:bg-white/10">{btn}</div>
            ))}
          </div>
          <div className="h-4 w-px bg-white/10" />
          <div className="flex gap-1.5">
            {['H1', 'H2'].map(btn => (
              <div key={btn} className="w-7 h-7 rounded bg-white/5 flex items-center justify-center text-[10px] text-brand-muted font-semibold cursor-pointer hover:bg-white/10">{btn}</div>
            ))}
          </div>
        </div>
        <div className="space-y-2 mb-3">
          <div className="h-2.5 rounded-full w-3/4" style={{ background: `${color}30` }} />
          <div className="h-2 rounded-full bg-white/10 w-full" />
          <div className="h-2 rounded-full bg-white/10 w-5/6" />
          <div className="h-2 rounded-full bg-white/10 w-full" />
          <div className="h-2 rounded-full bg-white/10 w-2/3" />
          <div className="mt-3 h-2.5 rounded-full w-1/2" style={{ background: `${color}30` }} />
          <div className="h-2 rounded-full bg-white/10 w-full" />
          <div className="h-2 rounded-full bg-white/10 w-4/5" />
        </div>
        <div className="flex items-center gap-2 text-xs text-brand-subtle">
          <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" className="w-3.5 h-3.5">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          <span>Edit and customise your document</span>
        </div>
      </div>
    ),

    'pipeline': (
      <div className="bg-[#0f1318] rounded-xl p-4 w-full max-w-[320px] border border-white/10">
        <div className="text-sm font-medium text-brand-light mb-3">Your Pipeline</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'In Progress', count: 8, items: ['Richardson', 'Mitchell'] },
            { label: 'Awaiting', count: 5, items: ['Thompson', 'Harris'] },
            { label: 'Complete', count: 12, items: ['Wilson', 'Brown'] },
          ].map((col, i) => (
            <div key={col.label} className="space-y-1.5">
              <div className="text-[10px] text-brand-subtle font-medium uppercase tracking-wider">{col.label}</div>
              <div className="text-lg font-bold" style={{ color: i === 0 ? color : 'rgba(255,255,255,0.5)' }}>{col.count}</div>
              {col.items.map(item => (
                <div key={item} className="text-[10px] text-brand-muted bg-white/5 rounded p-1.5 truncate">{item}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    ),

    'deadlines': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[280px] border border-white/10">
        <div className="text-sm font-medium text-brand-light mb-3">Upcoming Deadlines</div>
        <div className="space-y-2.5">
          {[
            { task: 'Offer expires - Richardson', days: '2 days', urgent: true },
            { task: 'Valuation due - Mitchell', days: '5 days', urgent: false },
            { task: 'Review meeting - Thompson', days: '1 week', urgent: false },
            { task: 'Completion - Harris', days: '2 weeks', urgent: false },
          ].map(item => (
            <div key={item.task} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.03]">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: item.urgent ? '#ef4444' : `${color}60` }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-brand-light truncate">{item.task}</div>
                <div className="text-[10px] text-brand-subtle">{item.days}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),

    'compliance': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[280px] border border-white/10">
        <div className="text-sm font-medium text-brand-light mb-3">Compliance Checklist</div>
        <div className="space-y-2">
          {[
            { text: 'Fact-find completed', done: true },
            { text: 'ID verification', done: true },
            { text: 'Income evidence', done: true },
            { text: 'Suitability letter drafted', done: false },
            { text: 'Client sign-off', done: false },
          ].map(item => (
            <div key={item.text} className="flex items-center gap-3 p-2 rounded-lg">
              <div className="w-5 h-5 rounded border flex items-center justify-center shrink-0" style={{ borderColor: item.done ? color : 'rgba(255,255,255,0.2)', background: item.done ? `${color}20` : 'transparent' }}>
                {item.done && (
                  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" className="w-3 h-3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <span className={`text-xs ${item.done ? 'text-brand-muted line-through' : 'text-brand-light'}`}>{item.text}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between text-xs">
            <span className="text-brand-subtle">Progress</span>
            <span style={{ color }}>60%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full mt-1.5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: '60%', background: color }} />
          </div>
        </div>
      </div>
    ),

    'analytics': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="text-sm font-medium text-brand-light mb-4">Performance</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Cases this month', value: '24' },
            { label: 'Avg. turnaround', value: '3.2d' },
            { label: 'Conversion rate', value: '68%' },
            { label: 'Revenue', value: '\u00a342k' },
          ].map(stat => (
            <div key={stat.label} className="bg-white/5 rounded-lg p-3 text-center">
              <div className="text-lg font-bold" style={{ color }}>{stat.value}</div>
              <div className="text-[10px] text-brand-subtle mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-1 h-10">
          {[4, 6, 3, 8, 5, 7, 9, 6, 8, 5, 7, 4].map((h, i) => (
            <div key={i} className="flex-1 rounded-t" style={{ height: h * 4, background: `${color}${i > 8 ? '40' : ''}` }} />
          ))}
        </div>
      </div>
    ),
  }

  return (
    <div className="flex items-center justify-center w-full">
      {mockups[mockupType] || <div className="text-brand-subtle text-sm">Preview</div>}
    </div>
  )
}

export default function FeatureWalkthroughModal({
  title,
  icon,
  steps,
  accentColor,
  accentGradient,
  onClose,
}: FeatureWalkthroughModalProps) {
  const [currentStep, setCurrentStep] = useState(1)

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const step = steps.find((s) => s.id === currentStep)
  if (!step) return null

  const progress = (currentStep / steps.length) * 100

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/90 flex items-start md:items-center justify-center overflow-y-auto py-4 px-3 md:p-6"
      onClick={onClose}
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-[#1a1f2e] rounded-2xl border border-white/[0.08] max-w-[900px] w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="p-5 md:p-6 shrink-0" style={{ background: accentGradient }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <ModalIcon type={icon} />
              </div>
              <div>
                <h2 className="font-serif text-lg md:text-xl font-medium text-white">{title}</h2>
                <p className="text-sm text-white/70">How it works &bull; {steps.length} steps</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white p-2 rounded-lg transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-5 md:px-6 py-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-brand-muted">
              Step {currentStep} of {steps.length}
            </span>
            <span className="text-sm font-medium" style={{ color: accentColor }}>
              {Math.round(progress)}% complete
            </span>
          </div>
          <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: accentGradient }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5 md:p-6 min-h-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mockup Area */}
            <div className="bg-[#15191f] rounded-xl p-6 flex items-center justify-center min-h-[200px]">
              <StepMockup mockupType={step.mockupType} color={accentColor} />
            </div>

            {/* Instructions */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white"
                  style={{ background: accentGradient }}
                >
                  {currentStep}
                </div>
                <h3 className="font-serif text-xl font-medium text-brand-light">{step.title}</h3>
              </div>

              <p className="text-[15px] text-brand-muted leading-relaxed mb-6">{step.description}</p>

              {/* Tip Box */}
              <div
                className="rounded-xl p-4 flex gap-3"
                style={{
                  background: `${accentColor}15`,
                  border: `1px solid ${accentColor}30`,
                }}
              >
                <div className="shrink-0">
                  <svg viewBox="0 0 24 24" fill={accentColor} className="w-5 h-5">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <span
                    className="text-xs font-semibold uppercase tracking-wider"
                    style={{ color: accentColor }}
                  >
                    Pro Tip
                  </span>
                  <p className="text-sm text-brand-muted mt-1">{step.tip}</p>
                </div>
              </div>

              {/* Step Indicators */}
              <div className="flex items-center justify-center gap-2 mt-6">
                {steps.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setCurrentStep(s.id)}
                    className="w-2.5 h-2.5 rounded-full transition-all duration-200"
                    style={{
                      background:
                        s.id === currentStep
                          ? accentColor
                          : s.id < currentStep
                            ? `${accentColor}80`
                            : 'rgba(255,255,255,0.1)',
                      transform: s.id === currentStep ? 'scale(1.3)' : 'scale(1)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between p-4 md:p-5 border-t border-white/[0.06] bg-black/20 shrink-0">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-brand-muted hover:text-brand-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Previous
          </button>

          {currentStep === steps.length ? (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: accentGradient }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              Complete
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: accentGradient }}
            >
              Next Step
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
